<script lang="ts">
export default {
    name: 'SidebarNestedSection',
};
</script>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useRoute } from 'vue-router/composables';

// @ts-expect-error not typed yet
import { useAppSidebarStore } from '@/store/pinia/app/sidebar.js';

const props = withDefaults(defineProps<{
    sectionName: string;
    sectionRoute: string;
    sectionActive: boolean;
    sectionIcon: object;
    nestedSections: object[];
    title: string;
    tooltip: string;
    goBackTitle: string;
    goBackTitleTooltip: string;
    reconnectTitle?: string;
    createTitle?: string;
    createIcon?: object;
    isActiveAuthorization?: boolean;
    isLocked?: boolean;
    nestedActive?: boolean;
    loading: boolean;
}>(), {
    createIcon: () => ({
        name: 'plus',
        prefix: 'fas',
    }),
});

const emit = defineEmits({
    create: null,
    reconnect: null,
    navigate: null,
});

const sidebarStore = useAppSidebarStore();
const { collapsed } = storeToRefs(sidebarStore);

const route = useRoute();

const selectedSection = computed(() => props.nestedActive || route.path.match(`/${props.sectionName}/[0-9]+/.*`) !== null);
const createDisabled = computed(() => props.isLocked || !props.isActiveAuthorization);

function create() {
    if (createDisabled.value) return;

    emit('create');
}
</script>

<template>
    <div
        class="flex w-full flex-col gap-y-2 transition-spacing duration-500 ease-in-out"
        :class="{ 'bg-black/40 py-2 shadow-inner shadow-black/30': selectedSection }"
    >
        <router-link
            class="flex-1 rounded-md font-semibold tracking-wide uppercase hover:bg-black/30 hover:text-white focus:text-white focus:outline-hidden"
            :class="{
                'text-white': sectionActive,
                'bg-black/40 shadow-inner shadow-black/30': sectionActive && !selectedSection,
                'text-gray-300': !sectionActive,
            }"
            :to="{ name: sectionRoute }"
            @click="emit('navigate')"
        >
            <div
                class="flex w-full items-center space-x-4 py-2 transition-spacing duration-500"
                :class="collapsed ? 'px-2' : 'px-4'"
                :title="selectedSection ? goBackTitleTooltip : tooltip"
            >
                <div class="flex h-6 w-6 shrink-0 items-center justify-center">
                    <base-icon
                        :name="selectedSection ? 'chevron-circle-left' : sectionIcon.name"
                        :prefix="selectedSection ? 'fas' : sectionIcon.prefix"
                    />
                </div>
                <span class="overflow-hidden leading-none whitespace-nowrap">
                    {{ selectedSection ? goBackTitle : title }}
                </span>
            </div>
        </router-link>
        <div
            v-if="selectedSection"
            class="flex flex-1 flex-col"
            :class="{
                'border-t border-gray-600': nestedSections?.length
            }"
        >
            <div v-if="!loading && !isActiveAuthorization && reconnectTitle" class="flex-1 py-2">
                <base-tooltip
                    class="w-full"
                    content="We've lost access to your platform. Please click here to re-establish the connection."
                >
                    <base-button
                        class="w-full"
                        type="warning"
                        content-class="flex h-5 max-w-full shrink-0 items-center justify-center overflow-hidden"
                        :custom-size-class="collapsed ? 'px-0.5 py-2': 'px-3 py-2'"
                        @click="emit('reconnect')"
                    >
                        <base-icon
                            class="transition-spacing duration-500 ease-in-out"
                            :class="collapsed ? 'mx-3' : 'mr-3'"
                            name="plug"
                            prefix="fas"
                        />
                        <div class="overflow-hidden leading-none font-bold tracking-wide uppercase">
                            {{ reconnectTitle }}
                        </div>
                    </base-button>
                </base-tooltip>
            </div>
            <div
                class="flex flex-col gap-y-2"
                :class="{
                    'border-t border-gray-700': !isActiveAuthorization && createTitle
                }"
            >
                <div v-if="createTitle" class="flex flex-col pt-2">
                    <base-button
                        type="primary"
                        content-class="flex h-5 max-w-full shrink-0 items-center justify-center overflow-hidden"
                        :custom-size-class="collapsed ? 'px-0.5 py-2': 'px-3 py-2'"
                        :title="collapsed ? createTitle : undefined"
                        :disabled="createDisabled"
                        @click="create"
                    >
                        <base-icon
                            class="shrink-0 transition-spacing duration-500 ease-in-out"
                            :class="collapsed ? 'mx-3' : 'mr-3'"
                            :name="createIcon.name"
                            :prefix="createIcon.prefix"
                            size="sm"
                        />
                        <div class="overflow-hidden pt-px leading-none font-bold tracking-wide whitespace-nowrap uppercase">
                            {{ createTitle }}
                        </div>
                    </base-button>
                </div>

                <div v-if="nestedSections" class="flex flex-col gap-y-1 border-t border-gray-700 pt-2">
                    <router-link
                        v-for="(button, index) in nestedSections"
                        v-slot="{ href, navigate, isActive }"
                        :key="index"
                        :to="button.route"
                        custom
                        @click="emit('navigate')"
                    >
                        <a
                            :href="href"
                            class="flex-1 rounded-md font-bold tracking-wide uppercase hover:bg-gray-700 hover:text-white focus:text-white focus:outline-hidden"
                            :title="collapsed ? button.label : undefined"
                            :class="{
                                'bg-gray-800 text-white': isActive,
                                'text-gray-300': !isActive,
                            }"
                            @click="navigate"
                        >
                            <div class="flex w-full items-center px-3 py-2">
                                <div
                                    class="flex h-4 w-4 shrink-0 items-center justify-center transition-spacing duration-500 ease-in-out"
                                    :class="collapsed ? 'mr-4' : 'mr-4'"
                                >
                                    <base-icon
                                        class="h-4 w-4"
                                        :name="button.icon.name"
                                        :prefix="button.icon.prefix"
                                    />
                                </div>
                                <span
                                    class="overflow-hidden text-sm leading-none whitespace-nowrap transition-size duration-500 ease-in-out"
                                    :class="collapsed ? 'md:w-0' : 'w-44'"
                                >
                                    {{ button.label }}
                                </span>
                            </div>
                        </a>
                    </router-link>
                </div>

                <slot name="under-section" />
            </div>
        </div>
    </div>
</template>
